import { EnhancedScrollArea } from "@components/enhanced-scroll-area";
import { CoinsVal } from "@components/icons/coins";
import { ResponsiveSelect } from "@components/responsive-select";
import UpgradeNeeded from "@components/upgrade-needed";
import { useImage } from "@features/images/use-images";
import { useRestyleOptions } from "@features/images/use-restyle";
import { useSavedState } from "@features/utils/use-saved-state";
import { CameraIcon } from "@heroicons/react/16/solid";
import { Button, Heading, Text, TextArea, Tooltip } from "@radix-ui/themes";
import { FileInputOrLink } from "@views/app/camera/components/file-input-or-link";
import { useState } from "react";
import { useTranslation } from "react-i18next";

interface StudioAIStylesProps {
  image: any;
  onAction: (action: string) => void;
}

export const StudioAIStyles = ({ image, onAction }: StudioAIStylesProps) => {
  const { t } = useTranslation();
  const [fromImage, setFromImage] = useState<File | string | null>(null);
  const { options } = useRestyleOptions();
  const { restyle } = useImage(image.id);
  const [selectedStyle, setSelectedStyle] = useState<string | null>(null);
  const [count, setCount] = useSavedState("restyle-count", 1);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleStyleSelect = (styleId: string) => {
    if (selectedStyle === styleId) {
      // If clicking on the same style, deselect it
      setSelectedStyle(null);
    } else {
      setSelectedStyle(styleId);
    }
  };

  const handleApplyStyle = async (
    styleId: string,
    fromImage?: File | string
  ) => {
    setIsProcessing(true);
    try {
      await restyle(styleId, fromImage, undefined, count);
      onAction(`apply_restyle_${styleId}`);
    } catch (error) {
      console.error("Failed to restyle image:", error);
    }
    setIsProcessing(false);
  };

  const selectedStyleData = options.data?.find((s) => s.id === selectedStyle);

  if (options.isLoading) {
    return (
      <div className="p-4 text-center text-gray-500">
        {t("studio.effects.ai_styles.loading")}
      </div>
    );
  }

  if (!options.data) {
    return (
      <div className="p-4 text-center text-red-500">
        {t("studio.effects.ai_styles.failed_to_load")}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Style Grid */}
      <div className="-mx-8 px-4 -mt-2">
        <EnhancedScrollArea className="w-full">
          <div className="flex gap-4 px-4 py-2">
            <Tooltip content={t("studio.effects.ai_styles.copy_style_tooltip")}>
              <div className="w-24 h-28 text-center space-y-2">
                <div
                  className={`cursor-pointer rounded-lg p-3 transition-all w-24 h-24 flex items-center justify-center bg-slate-100 dark:bg-slate-800 ${
                    selectedStyle === "custom"
                      ? "ring-2 ring-blue-500 ring-offset-0"
                      : ""
                  }`}
                  onClick={() => handleStyleSelect("custom")}
                >
                  <CameraIcon className="h-8 w-8" />
                </div>
                {/* Style Name */}
                <Text
                  size="1"
                  className="block whitespace-nowrap text-ellipsis overflow-hidden w-24"
                >
                  {t("studio.effects.ai_styles.custom")}
                </Text>
              </div>
            </Tooltip>
            {options.data.map((style) => (
              <div className="w-24 h-28 text-center space-y-2" key={style.id}>
                <div
                  className={`cursor-pointer rounded-lg p-3 transition-all w-24 h-24 ${
                    selectedStyle === style.id
                      ? "ring-2 ring-blue-500 ring-offset-0"
                      : ""
                  }`}
                  style={{
                    backgroundImage: `url("/images/styles/${style.id}.jpg")`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                  }}
                  onClick={() => handleStyleSelect(style.id)}
                />
                {/* Style Name */}
                <Text
                  size="1"
                  className="block whitespace-nowrap text-ellipsis overflow-hidden w-24"
                >
                  {style.name}
                </Text>
              </div>
            ))}
          </div>
        </EnhancedScrollArea>
      </div>

      {/* Selected style details */}
      {selectedStyle && (selectedStyleData || selectedStyle === "custom") && (
        <div className="space-y-4">
          {selectedStyleData && (
            <div>
              <Heading size="1" className="mb-2">
                {t("studio.effects.ai_styles.style_label")}{" "}
                {selectedStyleData.name}
              </Heading>
              <TextArea
                value={selectedStyleData.prompt}
                readOnly
                className="text-sm resize-none"
                rows={3}
                placeholder={t("studio.effects.ai_styles.style_placeholder")}
              />
            </div>
          )}

          {selectedStyle === "custom" && (
            <>
              <FileInputOrLink
                fromImage={fromImage}
                setFromImage={setFromImage}
              />
            </>
          )}

          <div className="flex gap-2">
            <ResponsiveSelect
              size="2"
              value={count.toString()}
              onChange={(value) => setCount(Number(value))}
              items={[
                { value: "1", label: "1" },
                { value: "2", label: "2" },
                { value: "3", label: "3" },
                { value: "4", label: "4" },
              ]}
              renderTrigger={() => (
                <span className="text-xs uppercase text-gray-500">{count}</span>
              )}
            />
            <UpgradeNeeded minimumCredits={count * 8}>
              <Button
                onClick={() =>
                  handleApplyStyle(selectedStyle, fromImage || undefined)
                }
                variant="solid"
                size="2"
                disabled={
                  !selectedStyle ||
                  isProcessing ||
                  (!fromImage && selectedStyle === "custom") ||
                  (!selectedStyleData && selectedStyle !== "custom")
                }
                className="flex-1"
              >
                <span>
                  {isProcessing
                    ? t("studio.effects.ai_styles.applying")
                    : t("studio.effects.ai_styles.apply")}{" "}
                  <CoinsVal val={8 * count} />
                </span>
              </Button>
            </UpgradeNeeded>
          </div>
        </div>
      )}
    </div>
  );
};
